/**
 * Avatar del usuario externo (R3.6).
 *
 * Renderiza la foto de perfil real cuando hay URL disponible (enriquecida via
 * Meta Graph API), con fallback a las iniciales si no hay foto o si la carga
 * falla (URL caducada, etc.). El browser cachea la imagen por URL.
 *
 * Hoy la mayoría de las conversaciones no tienen avatar (Zernio no lo entrega
 * y el acceso a la foto de perfil por Graph API es limitado) → cae a iniciales.
 * El componente está listo para mostrarlas apenas el backend provea la URL.
 */
import { useState } from "react";
import { cn } from "@/lib/cn";

function initialsFor(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

interface AvatarProps {
  name: string | null | undefined;
  src?: string | null;
  /** Tamaño en px (default 32). */
  size?: number;
  className?: string;
}

export function Avatar({ name, src, size = 32, className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = !!src && !failed;

  return (
    <div
      aria-hidden="true"
      style={{ width: size, height: size }}
      className={cn(
        "flex-shrink-0 rounded-full overflow-hidden",
        "bg-surface-3 border border-default",
        "flex items-center justify-center",
        "font-sans font-semibold text-text-secondary",
        className,
      )}
    >
      {showImage ? (
        <img
          src={src!}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span style={{ fontSize: Math.round(size * 0.4) }}>{initialsFor(name)}</span>
      )}
    </div>
  );
}
